import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { MessageCircle, X, Send, Loader2, Mail } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { TECH_EMAIL } from "@/lib/analysisMail";
import PAGES from "@/lib/seoPages.json";
import { API } from "@/lib/api";

const WA = "https://wa.me/41764411359?text=" + encodeURIComponent("Ciao AURORA-ADV, vorrei informazioni sui vostri servizi.");
const sid = () => {
  let s = sessionStorage.getItem("aurora_chatbot_sid");
  if (!s) { s = `cb_${crypto.randomUUID().replace(/-/g, "")}`; sessionStorage.setItem("aurora_chatbot_sid", s); }
  return s;
};

const QUICK = [
  ["Quanto costano i vostri pacchetti?", "How much do your packages cost?"],
  ["Quali sono i tempi di consegna?", "What are the delivery times?"],
  ["Come funziona l'audit della landing?", "How does the landing audit work?"],
];

const pageService = (pathname) => {
  const m = pathname.match(/^\/servizi\/([^/]+)/);
  return m && PAGES.find((x) => x.slug === m[1])?.title;
};

const WaIcon = (p) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...p}><path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.64.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.61-.92-2.2-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48 0 1.46 1.07 2.88 1.21 3.07.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.22 1.36.19 1.87.12.57-.09 1.76-.72 2-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35M12.05 21.5h-.01a9.4 9.4 0 0 1-4.8-1.31l-.34-.2-3.57.93.95-3.48-.22-.36a9.4 9.4 0 0 1-1.44-5.02c0-5.2 4.24-9.43 9.44-9.43a9.4 9.4 0 0 1 9.43 9.44c0 5.2-4.24 9.43-9.44 9.43m8.03-17.47A11.3 11.3 0 0 0 12.05.7C5.79.7.7 5.79.7 12.05c0 2 .52 3.95 1.52 5.67L.6 23.3l5.72-1.5a11.3 11.3 0 0 0 5.72 1.46h.01c6.26 0 11.35-5.09 11.35-11.35 0-3.03-1.18-5.89-3.32-8.03" /></svg>
);

export const WhatsAppButton = () => (
  <a href={WA} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-float" aria-label="WhatsApp"
    style={{ position: "fixed", bottom: 20, right: 20, zIndex: 9999 }}
    className="w-14 h-14 rounded-full bg-[#25D366] text-white flex items-center justify-center shadow-lg hover:scale-110 transition-transform">
    <WaIcon className="w-7 h-7" />
  </a>
);

const Fallback = ({ tr }) => (
  <div data-testid="chatbot-fallback" className="mt-2 flex flex-wrap gap-2">
    <a href={`mailto:${TECH_EMAIL}?subject=${encodeURIComponent("Richiesta informazioni AURORA-ADV")}`} data-testid="chatbot-contact-email" className="text-[11px] px-2.5 py-1 border border-[#2B3036] inline-flex items-center gap-1 hover:bg-[#2B3036] hover:text-white"><Mail className="w-3 h-3" /> {tr("Sì, contattami via email", "Yes, contact me by email")}</a>
    <a href={WA} target="_blank" rel="noopener noreferrer" data-testid="chatbot-contact-wa" className="text-[11px] px-2.5 py-1 bg-[#25D366] text-white inline-flex items-center gap-1"><WaIcon className="w-3 h-3" /> WhatsApp</a>
  </div>
);

export const ChatbotWidget = () => {
  const { lang, tr } = useLang();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [greeted, setGreeted] = useState(false);
  const { pathname } = useLocation();
  const end = useRef(null);
  useEffect(() => {
    if (open) sessionStorage.setItem("aurora_chatbot_autoopened", "1");
    if (!(pathname === "/" || pathname.startsWith("/servizi")) || open || sessionStorage.getItem("aurora_chatbot_autoopened")) return;
    const id = setTimeout(() => { sessionStorage.setItem("aurora_chatbot_autoopened", "1"); setGreeted(true); setOpen(true); }, 20000);
    return () => clearTimeout(id);
  }, [pathname, open]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);
  const service = pageService(pathname);
  const send = async (e, preset) => {
    e?.preventDefault();
    const m = (preset ?? text).trim();
    if (!m || busy) return;
    setText(""); setMsgs((x) => [...x, { role: "user", content: m }]); setBusy(true);
    try {
      const { data } = await axios.post(`${API}/chatbot`, { session_id: sid(), message: m, lang });
      setMsgs((x) => [...x, data.unknown ? { role: "assistant", unknown: true } : { role: "assistant", content: data.answer }]);
    } catch (err) {
      setMsgs((x) => [...x, { role: "assistant", unknown: true, error: err?.response?.data?.detail }]);
    } finally { setBusy(false); }
  };
  return (
    <>
      <button type="button" data-testid="chatbot-toggle" onClick={() => setOpen((o) => !o)} aria-label="AURORA-ADV chat"
        style={{ position: "fixed", bottom: 88, right: 20, zIndex: 9999 }}
        className="w-14 h-14 rounded-full bg-[#E0262C] text-white flex items-center justify-center shadow-lg hover:scale-110 transition-transform overflow-hidden border-2 border-white">
        {open ? <X className="w-6 h-6" /> : <img src="/brand/chatbot-robot.webp" alt="Assistente virtuale AURORA-ADV" className="w-full h-full object-cover scale-110" />}
      </button>
      {!open && (
        <span data-testid="chatbot-hint" style={{ position: "fixed", bottom: 102, right: 84, zIndex: 9999 }}
          className="hidden sm:block pointer-events-none bg-white border border-[#2B3036] text-[#2B3036] text-xs font-medium px-3 py-1.5 shadow-md animate-in fade-in duration-500">
          {tr("Chatta con l'assistente", "Chat with the assistant")}
        </span>
      )}
      {open && (
        <div data-testid="chatbot-panel" style={{ position: "fixed", bottom: 156, right: 20, zIndex: 9999 }}
          className="w-[calc(100vw-40px)] max-w-sm h-[460px] bg-white border border-[#2B3036] shadow-2xl flex flex-col animate-in fade-in slide-in-from-bottom-3 duration-300">
          <div className="px-4 py-3 bg-[#2B3036] text-white flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-[#E0262C]" /><span className="font-display text-sm">AURORA-ADV</span>
            <span className="ml-auto text-[10px] font-mono text-[#a1a1aa]">{tr("Risposte dal nostro listino", "Answers from our price list")}</span>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2 text-sm">
            <div className="bg-[#f4f6f7] p-2.5 text-[13px]">{tr("Ciao! Sono l'assistente AURORA-ADV. Chiedimi di pacchetti, prezzi, audit o tempi di consegna.", "Hi! I'm the AURORA-ADV assistant. Ask me about packages, prices, the audit or delivery times.")}</div>
            {greeted && (
              <div data-testid="chatbot-auto-greeting" className="mr-6 bg-[#E0262C] text-white p-2.5 text-[13px] font-medium">
                {service ? tr("Ciao! Ti interessa «{s}»? Posso aiutarti a scegliere il pacchetto giusto.", "Hi! Interested in «{s}»? I can help you choose the right package.").replace("{s}", service)
                  : tr("Ciao! Posso aiutarti a scegliere il pacchetto giusto?", "Hi! Can I help you choose the right package?")}
              </div>
            )}
            {msgs.length === 0 && (
              <div data-testid="chatbot-quick-questions" className="flex flex-col items-start gap-1.5 pt-1">
                {QUICK.map(([it, en], i) => (
                  <button key={i} type="button" data-testid={`chatbot-quick-${i}`} disabled={busy} onClick={() => send(null, tr(it, en))}
                    className="text-[12px] px-3 py-1.5 border border-[#2B3036] text-[#2B3036] hover:bg-[#2B3036] hover:text-white transition-colors text-left">{tr(it, en)}</button>
                ))}
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} data-testid={`chatbot-msg-${i}`} className={m.role === "user" ? "ml-8 bg-[#2B3036] text-white p-2.5 text-[13px]" : "mr-6 bg-[#f4f6f7] p-2.5 text-[13px] whitespace-pre-wrap"}>
                {m.unknown ? <>{m.error || tr("Non ho questa informazione. Vuoi parlare con un tecnico?", "I don't have this information. Would you like to talk to a technician?")}<Fallback tr={tr} /></> : m.content.replace(/\*\*/g, "")}
              </div>
            ))}
            {busy && <Loader2 className="w-4 h-4 animate-spin text-[#a1a1aa]" />}
            <div ref={end} />
          </div>
          <form onSubmit={send} className="border-t border-[#e4e4e7] p-2 flex gap-2">
            <input data-testid="chatbot-input" value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} placeholder={tr("Scrivi una domanda…", "Type a question…")} className="flex-1 h-10 px-3 bg-[#f4f6f7] text-sm outline-none focus:ring-1 focus:ring-[#2B3036]" />
            <button data-testid="chatbot-send" disabled={busy || !text.trim()} className="w-10 h-10 bg-[#E0262C] text-white flex items-center justify-center disabled:opacity-40"><Send className="w-4 h-4" /></button>
          </form>
        </div>
      )}
    </>
  );
};
